import type {ReactElement} from 'react'
import React, {useState, useEffect} from 'react'
import Image from 'next/image'
import {pushToDataLayer} from '@/utils/analytics'
import {ButtonIcons} from '@/_new-code/components/button-icons/button-icon'
import type {FilterState} from '@/_new-code/utilities/types'
import type {Tersed} from '@/_new-code/services/kontent-ai/types'
import {loadImageFromKontentAI} from '@/imageLoader'
import {TextButton} from '../flexible-web-toolkit/components/button/text-button'
import {EllipseButton} from '../flexible-web-toolkit/components/button/ellipse-button'
import type {IconContentItem} from '../flexible-web-toolkit/blocks/icon'
import {Icon} from '../flexible-web-toolkit/components/icon'
import styles from './map-filter.module.css'

interface FilterProps {
	businessFilterTitle: string
	buttonText: string
	filterOptions: FilterState
	resetText: string
	setFilterData: (data: {businessTypeIds: string[]; tagIds: string[]}) => void
	tagsFilterTitle: string
}

export const Filter = ({
	resetText = 'Reset Filters',
	buttonText = 'All Filters',
	filterOptions,
	businessFilterTitle = 'Filter By Business Type',
	tagsFilterTitle = 'Filter By Product',
	setFilterData,
}: FilterProps): ReactElement => {
	const [toggle, setToggle] = useState(false)
	const [filterOptionsList, setFilterOptionsList] = useState(filterOptions)

	useEffect(() => {
		const {businessTypes, tags} = filterOptionsList

		setFilterData({
			businessTypeIds: businessTypes
				.filter((item) => item.preselected_filter)
				.filter((item) => item.type_id.choice)
				.map((data) => data.type_id.choice),
			tagIds: tags
				.filter((item) => item.preselected_filter.boolean)
				.map((data) => data.pal_id),
		})
	}, [filterOptionsList, setFilterData])

	const resetFilters = (): void => {
		setFilterOptionsList(filterOptions)
		pushToDataLayer({
			event: 'finder_usage',
			finder_interaction: 'Filter Cleared',
			finder_url: window.location.href,
		})
	}

	const filterBusinessTypes = (
		data: {
			display_name: string
			preselected_filter: boolean
			type_id: {
				choice: string | undefined
			}
		},
		value: boolean
	): void => {
		const {businessTypes = []} = filterOptionsList
		setFilterOptionsList({
			...filterOptionsList,
			businessTypes: [
				...businessTypes.map((obj) => {
					if (obj.type_id.choice === data.type_id.choice)
						return {...obj, preselected_filter: value}
					return obj
				}),
			],
		})

		pushToDataLayer({
			event: 'finder_usage',
			finder_interaction: value
				? 'Filter Selected-Business Type'
				: 'Filter Cleared',
			finder_url: window.location.href,
		})
	}

	const filterProductTags = (
		data: {
			preselected_filter: {
				boolean: boolean
			}
			pal_id: string
			display_name: string
		},
		value: boolean
	): void => {
		const {tags = []} = filterOptionsList
		setFilterOptionsList({
			...filterOptionsList,
			tags: [
				...tags.map((obj) => {
					if (obj.pal_id === data.pal_id)
						return {...obj, preselected_filter: {boolean: value}}
					return obj
				}),
			],
		})

		pushToDataLayer({
			event: 'finder_usage',
			finder_interaction: value
				? 'Filter Selected-Product'
				: 'Filter Cleared',
			finder_url: window.location.href,
		})
	}

	const getURLAndDescription = (option: {
		display_name: string
		preselected_filter: boolean
		type_id: {
			choice: string | undefined
		}
		icons?: Tersed<IconContentItem>[]
	}): {imageURL: string; description: string} => {
		let imageURL = ''
		let description = ''

		if (option.icons && option.icons.length > 0) {
			const icon = option.icons[0]
			// Extract image from elements.icon
			if (icon?.elements.icon && icon.elements.icon.length > 0) {
				imageURL = icon.elements.icon[0]?.url ?? ''
				description = icon.elements.icon[0]?.description ?? ''
			}
		}
		return {imageURL, description}
	}

	return (
		<div className="flex flex-col items-center pb-4 text-xs md:flex-row md:space-x-4">
			<div className="mr-4">
				<TextButton
					className="hidden not-italic text-black md:block"
					onClick={resetFilters}
				>
					{resetText}
				</TextButton>
			</div>
			<div className="relative w-full">
				<div
					className="flex w-full min-w-[280px] items-center justify-between rounded border border-elanco-blue bg-gray-200 px-5 py-[14px] text-left text-sm font-semibold placeholder-black"
					onClick={() => {
						setToggle(!toggle)
					}}
					role="presentation"
				>
					{buttonText}
					<EllipseButton
						icon={ButtonIcons.ChevronDown}
						noIconAnimation
						size="sm"
					/>
				</div>
				{toggle ? (
					<div className="absolute z-10 max-h-96 w-full overflow-auto rounded-md bg-white px-5 py-4 shadow-[0_10px_15px_-3px_rgb(0_0_0_/_10%),_0_4px_6px_-4px_rgb(0_0_0_/_10%)]">
						{filterOptionsList.businessTypes.length > 1 && (
							<>
								<h2 className="text-sm">
									{businessFilterTitle}
								</h2>
								<ul className="mb-4">
									{filterOptionsList.businessTypes
										.filter(
											(businessType) =>
												businessType.type_id.choice &&
												businessType.type_id.choice
													.length > 0
										)
										.map((option, idx) => {
											const {imageURL, description} =
												getURLAndDescription(option)
											const displayName =
												option.display_name || ''
											return (
												<li key={idx}>
													<input
														checked={
															option.preselected_filter
														}
														className={styles.input}
														id={`${displayName.trim()}-${idx}`}
														name={displayName}
														onChange={(e) => {
															filterBusinessTypes(
																option,
																e.target.checked
															)
														}}
														type="checkbox"
													/>
													<label
														className="my-2 text-black"
														htmlFor={`${displayName.trim()}-${idx}`}
													>
														{imageURL ? (
															<Icon
																className="h-10 w-6 justify-center"
																noBorder
																noShadow
																size="xxs"
															>
																<Image
																	alt={
																		description
																	}
																	height={30}
																	loader={
																		loadImageFromKontentAI
																	}
																	src={
																		imageURL
																	}
																	width={24}
																/>
															</Icon>
														) : null}
														<span>
															{displayName}
														</span>
													</label>
												</li>
											)
										})}
								</ul>
							</>
						)}
						{filterOptionsList.tags.length > 1 && (
							<>
								<h2 className="text-sm">{tagsFilterTitle}</h2>
								<ul className="mb-4">
									{filterOptionsList.tags
										.filter(
											(tag) =>
												tag.pal_id &&
												tag.pal_id.length > 0
										)
										.map((option, idx) => {
											const displayName =
												option.display_name || ''
											return (
												<li key={idx}>
													<input
														checked={
															option
																.preselected_filter
																.boolean
														}
														className={styles.input}
														id={`${displayName.trim()}-${idx}`}
														name={displayName}
														onChange={(e) => {
															filterProductTags(
																option,
																e.target.checked
															)
														}}
														type="checkbox"
													/>
													<label
														className="my-2 text-black"
														htmlFor={`${displayName.trim()}-${idx}`}
													>
														<span>
															{displayName}
														</span>
													</label>
												</li>
											)
										})}
								</ul>
							</>
						)}
						<div className="flex items-center justify-between">
							<TextButton
								className="not-italic text-black"
								onClick={resetFilters}
							>
								{resetText}
							</TextButton>

							<EllipseButton
								icon={ButtonIcons.Close}
								noIconAnimation
								onClick={() => {
									setToggle(false)
								}}
								size="sm"
							/>
						</div>
					</div>
				) : null}
			</div>
		</div>
	)
}
